import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/console";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { useLoadedPermission } from "@/hooks/use-permission";
import { usePlainErrorMessage } from "@/components/data-display/api-failure";
import { rolesGrantingPermission } from "@/lib/permissions";
import { useWebhooks } from "@/hooks/use-webhooks";
import { useAuthStore } from "@/stores/auth-store";

/* DEV-00 — Developer Home.
   The page intentionally renders only data backed by effective-access and
   GET /v1/webhooks. API activity, service health, version, and rate-limit
   sources do not exist, so those states are explicit rather than inferred. */

const QUICKNAV = [
  { icon: "🔑", title: "API Keys", desc: "Manage API credentials and scopes", href: "/developer/api-keys" },
  { icon: "⚡", title: "Webhooks", desc: "Configure event delivery endpoints", href: "/developer/webhooks" },
];

/* DEV-103 (flow: read the effective scopes of the current principal). The
   scopes this page itself depends on, so the panel can show an absent one as
   absent instead of assuming it. Both are read from the same effective-access
   answer the rest of the page uses; nothing here grants or changes access. */
const REQUIRED_SCOPES = [
  { scope: "wallets:read", reason: "The Quick Start example reads the wallet list." },
  { scope: "webhooks:manage", reason: "The Webhooks panel and the webhooks page." },
];

const DENIED_REASON_CODES = new Set([
  "AUTH_INSUFFICIENT_SCOPE",
  "AUTH_INSUFFICIENT_PERMISSION",
  "AUTH_TENANT_MISMATCH",
  "AUTH_TRUST_DOMAIN_MISMATCH",
]);

function displayValue(value: string): string {
  return value.trim() || "—";
}

function quickStartCode(
  baseUrl: string,
  context: { organizationId: string; tenantId: string; environment: string; trustDomain: string },
): string {
  return `curl --request GET '${baseUrl}/v1/walletkit/wallets' \\
  --header 'X-Alloy-Api-Key: YOUR_API_KEY' \\
  --header 'X-Alloy-Organization-Id: ${displayValue(context.organizationId)}' \\
  --header 'X-Alloy-Tenant-Id: ${displayValue(context.tenantId)}' \\
  --header 'X-Alloy-Environment: ${displayValue(context.environment)}' \\
  --header 'X-Alloy-Trust-Domain: ${displayValue(context.trustDomain)}' \\
  --header 'X-Alloy-Correlation-Id: developer-example-0001'`;
}

export function DeveloperHome() {
  const plainError = usePlainErrorMessage();
  const experience = useAuthStore((state) => state.experience);
  const webhookAccess = useLoadedPermission("webhook:manage", "webhooks:manage");
  const { webhooks, loading, error, refresh } = useWebhooks(webhookAccess.loaded && webhookAccess.allowed);
  const baseUrl = typeof window === "undefined" ? "" : window.location.origin;
  const apiDenied = error ? DENIED_REASON_CODES.has(error.reasonCode) : false;
  // DEV-03 (UX audit D09): a viewer without webhook access is told which role
  // carries it rather than only that they are denied, so they know what to ask
  // for. Labels come from the shared role catalog; nothing here changes who is
  // allowed to see webhooks.
  const webhookRoles = rolesGrantingPermission("webhook:manage");
  // DEV-103: only a loaded effective-access answer (source "api") can say what
  // this session holds. A fixture or an unloaded contract is "not known", which
  // is a different answer from "no scopes" and is rendered as such below.
  const accessLoaded = experience.source === "api";
  const grantedScopes = Array.from(new Set(experience.grantedScopes)).sort();
  const grantedSummary =
    grantedScopes.length === 0
      ? "No scopes are granted to this session."
      : `${grantedScopes.length} ${grantedScopes.length === 1 ? "scope" : "scopes"} granted to this session.`;
  const context = {
    organizationId: experience.activeOrganization.id,
    tenantId: experience.activeOrganization.tenantId,
    environment: experience.environment.mode || "—",
    trustDomain: experience.activeOrganization.trustDomainId,
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Developers</h1>
        <p className="mt-1 text-sm text-muted">Use the active organization context to integrate with Alloy&apos;s public API.</p>
      </div>

      <div className="mb-5 grid gap-3.5 sm:grid-cols-2">
        {QUICKNAV.map((item) => (
          <Link key={item.title} to={item.href} className="flex flex-col gap-2 rounded-xl border border-line bg-surface p-5 transition-shadow hover:border-primary hover:shadow-md">
            <div className="text-[22px]" aria-hidden>{item.icon}</div>
            <div className="text-sm font-bold text-ink">{item.title}</div>
            <div className="text-xs text-muted">{item.desc}</div>
          </Link>
        ))}
      </div>

      <div className="grid min-w-0 gap-5 xl:grid-cols-[1fr_340px]">
        <div className="min-w-0">
          <section className="mb-4 rounded-xl border border-line bg-surface p-5" aria-labelledby="developer-environment-heading">
            <h2 id="developer-environment-heading" className="mb-3.5 text-sm font-bold text-ink">Environment</h2>
            <dl className="space-y-0">
              <EnvRow label="Current Environment"><Badge variant="warning" size="sm">{displayValue(context.environment)}</Badge></EnvRow>
              <EnvRow label="Base URL"><span className="font-mono text-xs text-primary">{displayValue(baseUrl)}</span></EnvRow>
              <EnvRow label="Organization ID"><span className="font-mono text-xs text-ink">{displayValue(context.organizationId)}</span></EnvRow>
              <EnvRow label="Trust Domain"><span className="font-mono text-xs text-ink">{displayValue(context.trustDomain)}</span></EnvRow>
            </dl>
            {/* DEV-03 (UX audit D09): four separate "Not available yet" rows
                (API version, rate limit, recent activity, service health)
                collapsed into one compact muted line, so the absence of those
                sources is stated once instead of repeated per field. */}
            <p className="mt-2 border-t border-line pt-2.5 text-xs text-muted" data-testid="developer-unavailable-summary">
              API version, rate limits and recent activity are not shown yet.
            </p>
          </section>

          <section className="mb-4 rounded-xl border border-line bg-surface p-5" aria-labelledby="developer-access-heading">
            <h2 id="developer-access-heading" className="mb-3.5 text-sm font-bold text-ink">Your access</h2>
            {accessLoaded ? (
              <>
                <p className="text-sm text-ink" data-testid="developer-access-summary">{grantedSummary}</p>
                {grantedScopes.length > 0 && (
                  <ul className="mt-2 space-y-1" data-testid="developer-access-scopes">
                    {grantedScopes.map((scope) => (
                      <li key={scope} className="font-mono text-xs text-ink">{scope}</li>
                    ))}
                  </ul>
                )}
                {/* A scope the session does not hold is rendered as Not granted,
                    never omitted: an absent scope has to be visible as absent. */}
                <dl className="mt-3 border-t border-line pt-1" data-testid="developer-access-required">
                  {REQUIRED_SCOPES.map((required) => (
                    <AccessScopeRow
                      key={required.scope}
                      scope={required.scope}
                      reason={required.reason}
                      granted={grantedScopes.includes(required.scope)}
                    />
                  ))}
                </dl>
              </>
            ) : (
              <p className="text-sm text-ink" data-testid="developer-access-summary">
                <span className="font-mono">—</span>{" "}
                <span className="text-muted">Your granted scopes could not be loaded.</span>
              </p>
            )}
            <p className="mt-3 border-t border-line pt-2.5 text-xs text-muted" data-testid="developer-access-session-note">
              Scopes are read when you sign in. A scope granted after that appears here after you sign in again.
            </p>
          </section>

          <section className="mb-4 overflow-hidden rounded-xl border border-line bg-surface" aria-labelledby="developer-webhooks-heading">
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <h2 id="developer-webhooks-heading" className="text-sm font-bold text-ink">Webhooks</h2>
              {webhookAccess.allowed && !loading && !error && (
                <span className="text-xs text-muted">{webhooks.length} shown</span>
              )}
            </div>

            {!webhookAccess.loaded && <PanelState>Checking webhook access…</PanelState>}
            {webhookAccess.loaded && !webhookAccess.allowed && (
              <DeniedLine roles={webhookRoles}>
                You don&apos;t have permission to view webhooks for this organization.
              </DeniedLine>
            )}
            {webhookAccess.allowed && loading && <PanelState>Loading webhooks…</PanelState>}
            {webhookAccess.allowed && !loading && apiDenied && (
              <DeniedLine>You don&apos;t have permission to view webhooks for this organization.</DeniedLine>
            )}
            {webhookAccess.allowed && !loading && error && !apiDenied && (
              <div className="px-5 py-7 text-center">
                <p className="text-sm text-danger">Failed to load webhooks. {plainError(error)}</p>
                {error.reasonCode && <p className="mt-1 font-mono text-xs text-muted">Code: {error.reasonCode}</p>}
                {/* D636: the same shared support-reference block every other
                    error surface renders, so the reference the response
                    carried is never dropped on this one. Renders nothing
                    when the response carried none. */}
                <SupportDetails reference={error.supportReference} capability={error.capability} className="items-center" />
                <Button type="button" variant="secondary" size="sm" className="mt-3" onClick={refresh}>Try again</Button>
              </div>
            )}
            {webhookAccess.allowed && !loading && !error && webhooks.length === 0 && (
              <PanelState>No webhooks configured.</PanelState>
            )}
            {webhookAccess.allowed && !loading && !error && webhooks.length > 0 && (
              <ul className="divide-y divide-line">
                {webhooks.map((webhook) => (
                  <li key={webhook.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                    <div className="min-w-0">
                      <div className="truncate font-mono text-xs text-ink">{webhook.url}</div>
                      <div className="mt-1 text-xs text-muted">{webhook.eventTypes.length > 0 ? webhook.eventTypes.join(", ") : "No event types configured"}</div>
                    </div>
                    <Badge variant={webhook.active ? "success" : "neutral"} size="sm">{webhook.active ? "Active" : "Disabled"}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="min-w-0">
          <section className="mb-4 rounded-xl border border-line bg-surface p-5" aria-labelledby="developer-quick-start-heading">
            <h2 id="developer-quick-start-heading" className="mb-3.5 text-sm font-bold text-ink">Quick Start</h2>
            <p className="mb-1.5 text-xs font-medium text-ink">List your wallets</p>
            <CodeBlock code={quickStartCode(baseUrl, context)} className="mb-3" />
            <p className="mb-4 text-xs leading-relaxed text-muted">
              API keys require the complete context above. Mixing a key with an OIDC bearer returns <span className="font-mono">AUTH_MALFORMED_CREDENTIAL</span>; incomplete context returns <span className="font-mono">403 AUTH_TENANT_MISMATCH</span>.
            </p>
            <Link to="/developer/api-keys" className={buttonVariants({ variant: "secondary", className: "mb-2 w-full justify-center" })}>
              Manage API Keys →
            </Link>
            <Link to="/developer/webhooks" className={buttonVariants({ variant: "secondary", className: "w-full justify-center" })}>
              Configure Webhooks →
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
}

function EnvRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-1 border-b border-line py-2 text-sm last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 max-w-full break-all text-left sm:text-right">{children}</dd>
    </div>
  );
}

/**
 * DEV-103: one required scope of this page, with the state the session holds it
 * in. The scope string and the badge are always both rendered, so a viewer can
 * read either the scope they have or the scope they are missing.
 */
function AccessScopeRow({ scope, reason, granted }: { scope: string; reason: string; granted: boolean }) {
  return (
    <div className="flex flex-col items-start gap-1 border-b border-line py-2 text-sm last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <dt className="min-w-0 max-w-full break-all">
        <span className="font-mono text-xs text-ink">{scope}</span>
        <span className="mt-0.5 block text-xs text-muted">{reason}</span>
      </dt>
      <dd className="text-left sm:text-right">
        <Badge variant={granted ? "success" : "neutral"} size="sm">{granted ? "Granted" : "Not granted"}</Badge>
      </dd>
    </div>
  );
}

function PanelState({ children }: { children: React.ReactNode }) {
  return <div className="px-5 py-8 text-center text-sm text-muted" role="status">{children}</div>;
}

/**
 * DEV-03 (UX audit D09): the permission-denied surface, rendered as a single
 * compact muted line instead of a large centred status block. When the viewer
 * simply lacks the permission, `roles` names the roles that carry it (from the
 * shared role catalog) so they know what to request; it is omitted when the
 * viewer already holds the permission and the gateway still refused.
 */
function DeniedLine({ children, roles }: { children: React.ReactNode; roles?: string[] }) {
  return (
    <p className="px-5 py-3.5 text-sm text-muted" role="status" data-testid="developer-webhooks-denied">
      {children}
      {roles && roles.length > 0 && <> This requires one of these roles: {roles.join(", ")}.</>}
    </p>
  );
}
